import NiceModal from "@ebay/nice-modal-react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../../../components/core/basic/PageContainer";
import Card from "../../../components/core/basic/Card";
import { css } from "../../../../styled-system/css";
import { Tab, TabList, TabPanel, Tabs } from "../../../components/core/basic/Tabs";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { EntityTypeSidebar } from "../../../modules/tags/components/EntityTypeSidebar";
import { TagTable } from "../../../modules/tags/components/TagTable";
import { TagFormModal } from "../../../modules/tags/components/TagFormModal";
import { tagEntityTypeSchema, type TagEntityType } from "../../../zodios/apiDefinitions/api/tags";
import { FaArrowLeft, FaPlus } from "react-icons/fa6";
import { useSimpleCan } from "../../../config/casl/casl";
import UnauthorizedWarning from "../../../components/core/basic/UnauthorizedWarning";
import useQueryParams from "../../../hooks/useQueryParams";

type TabKey = "user" | "system";

const isValidTabKey = (key: unknown): key is TabKey => key === "user" || key === "system";

function TagManagement() {
  const { t } = useTranslation(["tags"]);
  const navigate = useNavigate();
  const userCanReadTags = useSimpleCan("read", "Tag");
  const userCanCreateTags = useSimpleCan("create", "Tag");
  const [entityTypeParam, setEntityType] = useQueryParams("entity", "ADVERTISEMENT");
  const [activeTabParam, setActiveTab] = useQueryParams("tab", "user");

  const entityType: TagEntityType =
    tagEntityTypeSchema.safeParse(entityTypeParam).success ? (entityTypeParam as TagEntityType) : "ADVERTISEMENT";
  const activeTab: TabKey = isValidTabKey(activeTabParam) ? activeTabParam : "user";

  const handleCreateTag = () => {
    void NiceModal.show(TagFormModal, { entityType });
  };

  return (
    <PageContainer>
      {userCanReadTags ?
        <VStack
          gap="medium"
          alignItems="stretch"
          width="100%"
          paddingY="medium"
          paddingX={{ base: "small", md: "medium" }}
        >
          <HStack justifyContent="space-between" alignItems="center">
            <Button
              variant="link"
              onPress={() => void navigate({ to: "/user/settings" })}
              className={css({ fontSize: "medium" })}
            >
              <FaArrowLeft /> {t(($) => $.tags["Back to Settings"])}
            </Button>
            <h1 className={css({ fontSize: "xxlarge", fontWeight: "bold" })}>{t(($) => $.tags["Tag Management"])}</h1>
            <div />
          </HStack>
          <Card>
            <Flex gap="large" padding="medium">
              <EntityTypeSidebar value={entityType} onChange={setEntityType} />
              <VStack flex="1" gap="medium" alignItems="stretch" minWidth={0}>
                <Tabs
                  selectedKey={activeTab}
                  onSelectionChange={(key) => {
                    if (isValidTabKey(key)) {
                      setActiveTab(key);
                    }
                  }}
                >
                  <HStack justifyContent="space-between" alignItems="center">
                    <TabList aria-label={t(($) => $.tags["Tag types"])}>
                      <Tab id="user">{t(($) => $.tags["User Tags"])}</Tab>
                      <Tab id="system">{t(($) => $.tags["System Tags"])}</Tab>
                    </TabList>
                    {activeTab === "user" && userCanCreateTags && (
                      <Button onPress={handleCreateTag} variant="primary">
                        <FaPlus /> {t(($) => $.tags["Create Tag"])}
                      </Button>
                    )}
                  </HStack>

                  <TabPanel id="user">
                    <TagTable entityType={entityType} tagType="user" />
                  </TabPanel>

                  <TabPanel id="system">
                    <TagTable entityType={entityType} tagType="system" />
                  </TabPanel>
                </Tabs>
              </VStack>
            </Flex>
          </Card>
        </VStack>
      : <UnauthorizedWarning />}
    </PageContainer>
  );
}

export const Route = createFileRoute("/_authenticated/user/tagmanagement")({
  component: TagManagement,
  staticData: { application: "Admin" },
});
